/**
 * Service detail hero: split layout with the service image on the left and a booking
 * card on the right (title + rating, price, description, duration, add-ons link, Book
 * Service CTA, trust badges, What-to-Expect / How-to-Prep accordions, and availability).
 * Styled to match the Figma "Service Page" design (node 1491:4438).
 */
/* eslint-disable @next/next/no-img-element */
import React from 'react'
import Image from 'next/image'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq, formatDurationDisplay } from '../../helpers'
import { useMarketContext } from '../../components/MarketContext'
import { useMarketPrice } from '../../helpers/useMarketPrice'
import { bookServiceHref } from '../../helpers/bookLink'
import { trackServiceDetailBookClicked } from '../../helpers/tracking'
import Accordion from './Accordion'

const DEFAULT_HERO_IMAGE = '/services/blowout.jpg'

/**
 * Splits a multi-line info string into trimmed, non-empty bullet lines.
 * @param {string} [text]
 * @returns {string[]}
 */
const toBullets = text => (text || '').split('\n').map(line => line.trim()).filter(Boolean)

const TRUST_BADGES = [
  { icon: '/service-detail/trust-license.svg', text: 'Licensed,\nbackground-checked\nbeauty pros' },
  { icon: '/service-detail/trust-background.svg', text: '2+ yrs experience,\ntop-rated by clients' },
  { icon: '/service-detail/trust-sameday.svg', text: 'Same-day\nappointments available' },
]

/**
 * Trust badges list. Rendered twice — under the hero image on desktop and under the
 * Book Service button on mobile — with only one visible per breakpoint via className.
 * @param {{ className?: string }} props
 */
const TrustBadges = ({ className }) => (
  <ul className={className}>
    {TRUST_BADGES.map(({ icon, text }) => (
      <li key={icon}>
        <img src={icon} alt="" aria-hidden="true" width={48} height={48} />
        <span>{text}</span>
      </li>
    ))}
  </ul>
)

/**
 * @param {{ service: object, availableCities: string[], addOnCount?: number }} props
 */
const HeroBookingCard = ({ service, availableCities = [], addOnCount = 0 }) => {
  const { selectedMarket } = useMarketContext()
  const { priceText } = useMarketPrice(service)
  const rating = service.serviceDetailRating ?? 4.9
  const href = bookServiceHref({ service, market: selectedMarket })
  const image = service.mobileImageUrl || DEFAULT_HERO_IMAGE
  const whatToExpect = toBullets(service.infoWhatsIncluded)
  const howToPrep = toBullets(service.infoHowToPrep)

  return (
    <Wrapper>
      <div className="media">
        <div className="image">
          <div className="image-frame">
            <Image
              src={image}
              alt={service.name}
              fill
              sizes="(max-width: 900px) 100vw, 50vw"
              style={{ objectFit: 'cover', objectPosition: 'center bottom' }}
              priority
            />
          </div>
        </div>

        <TrustBadges className="badges badges--media" />
      </div>

      <div className="card">
        <div className="intro">
          <div className="title-row">
            <h1>{service.name}</h1>
            <span className="rating" aria-label={`Rated ${rating} out of 5`}>
              <img src="/service-detail/star-filled.svg" alt="" aria-hidden="true" width={24} height={24} />
              {rating}
            </span>
          </div>
          {priceText && <p className="price">Starting at {priceText}</p>}
          {service.description && <p className="tagline">{service.description}</p>}
        </div>

        <div className="meta">
          {service.duration != null && (
            <span className="meta-row">
              <img src="/service-detail/icon-duration.svg" alt="" aria-hidden="true" width={24} height={24} />
              {formatDurationDisplay(service.duration, service)} mins
            </span>
          )}
          {addOnCount > 0 && (
            <a className="meta-row addons" href="#featured-addons">
              <img src="/service-detail/icon-addon.svg" alt="" aria-hidden="true" width={24} height={24} />
              <span>{addOnCount} Add-on{addOnCount === 1 ? '' : 's'} available</span>
            </a>
          )}
        </div>

        <a
          className="book-button"
          href={href}
          onClick={() => trackServiceDetailBookClicked({
            serviceId: service.id,
            serviceName: service.name,
            serviceSlug: service.slug,
            market: selectedMarket?.shortName,
          })}
        >
          Book Service
        </a>

        <TrustBadges className="badges badges--card" />

        <div className="accordions">
          {whatToExpect.length > 0 && (
            <Accordion title="What to Expect" defaultOpen>
              <ul className="bullets">
                {whatToExpect.map((line, i) => <li key={`${line}-${i}`}>{line}</li>)}
              </ul>
            </Accordion>
          )}
          {howToPrep.length > 0 && (
            <Accordion title="How to Prep">
              <ul className="bullets">
                {howToPrep.map((line, i) => <li key={`${line}-${i}`}>{line}</li>)}
              </ul>
            </Accordion>
          )}
        </div>

        {availableCities.length > 0 && (
          <p className="cities">
            <span className="label">Available in</span> {availableCities.join('  |  ')}
          </p>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  background: ${c.white};
  padding-top: 40px;

  ${mq('max', '900px')} {
    grid-template-columns: minmax(0, 1fr);
    padding-top: 0;
  }

  /*
   * Left column holds the image and, beneath it, the trust badges. It carries the
   * page's 88px left gutter so both line up with the sections below; on mobile the
   * image is full-bleed and the badges get the standard 24px side padding.
   */
  .media {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-left: 88px;
    ${mq('max', '900px')} {
      padding-left: 0;
    }
  }

  /*
   * The hero image is a fixed landscape crop, top-anchored (align-self:start on the
   * grid), so it never stretches or scales when the right-hand accordions expand. The
   * source asset is landscape, so web matches it rather than cropping to a square.
   */
  .image-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 5 / 3;
    background: #c8c8cc;
    ${mq('max', '900px')} {
      aspect-ratio: 4 / 3;
    }
  }

  .card {
    min-width: 0;
    padding: 20px 104px 40px 72px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    ${mq('max', '900px')} {
      padding: 32px 24px 40px;
      gap: 20px;
    }
  }

  .intro {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  h1 {
    font-family: Garnett;
    font-size: 36px;
    font-weight: 700;
    line-height: 40px;
    text-transform: capitalize;
    color: ${c.black};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 28px;
      line-height: 32px;
    }
  }

  .rating {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: Euclid;
    font-size: 18px;
    color: ${c.black};
    white-space: nowrap;
  }

  .price {
    font-family: Euclid;
    font-size: 18px;
    color: ${c.black};
    margin: 0;
  }

  .tagline {
    font-family: Euclid;
    font-size: 18px;
    line-height: 24px;
    color: ${c.darkGrey};
    margin: 0;
    max-width: 456px;
  }

  .meta {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .meta-row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: Euclid;
    font-size: 18px;
    color: ${c.black};
    width: max-content;
  }

  .addons {
    text-decoration: none;
    span {
      text-decoration: underline;
    }
  }

  .book-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    background: ${c.cobalt};
    color: ${c.white};
    font-family: Euclid;
    font-size: 18px;
    font-weight: 600;
    text-decoration: none;
    transition: background-color 0.2s ease;

    &:hover {
      background: #0a2bb5;
    }
  }

  .badges {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 0;
    margin: 0;
  }

  /* Desktop shows the badges under the hero image; the in-card copy is hidden. */
  .badges--card {
    display: none;
  }

  .badges li {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
  }

  .badges span {
    font-family: Euclid;
    font-size: 13px;
    line-height: 16px;
    color: ${c.darkGrey};
    white-space: pre-line;
  }

  ${mq('max', '900px')} {
    /* Mobile moves the badges under the Book Service button, inside the card. */
    .badges--media {
      display: none;
    }

    .badges--card {
      display: grid;
      grid-template-columns: 1fr;
      justify-items: start;
    }

    .badges--card li {
      flex-direction: row;
      text-align: left;
    }

    .badges--card span {
      white-space: normal;
    }
  }

  .accordions {
    display: flex;
    flex-direction: column;
  }

  .bullets {
    margin: 0;
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cities {
    font-family: Euclid;
    font-size: 13px;
    line-height: 20px;
    color: ${c.darkGrey};
    margin: 0;
  }

  .cities .label {
    font-weight: 600;
    color: ${c.black};
  }
`

export default HeroBookingCard
